import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Archive, Clock, User, FileText, MessageSquare, Paperclip, Edit2, Check, X, Trash2, MoreVertical, CalendarIcon, Repeat, Plus, ChevronDown, ChevronUp, CircleCheckBig } from "lucide-react";
import TaskMessageComposer from "./TaskMessageComposer";
import ReactMarkdown from "react-markdown";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TimeTracker } from "./TimeTracker";
import { AssigneeSelector } from "./AssigneeSelector";
import { Task, TaskPriority, Message as TaskMessage, Attachment as TaskAttachment, Column } from "./types";
import { format, formatDistanceToNow, startOfDay, isBefore } from "date-fns";
import { cn } from "@/lib/utils";
import { isTaskOverdue } from "@/lib/task-utils";
import { useWorkspace } from "@/context/WorkspaceContext";
import { fetchWorkspaceMembers, WorkspaceMemberWithProfile } from "@/lib/workspaceMembers";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { uploadToR2, deleteFromR2 } from "@/lib/r2-storage";
import { taskService } from "@/lib/taskService";

interface TaskDetailDialogProps {
  task: Task | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onArchive?: (id: string) => void;
  onDelete?: (id: string) => void;
  onUpdateTask?: (id: string, updates: Partial<Task>) => void;
  columnMeta?: Record<string, Column>;
  boardName?: string;
  enableUpvotes?: boolean;
  onToggleUpvote?: (id: string) => void;
  currentUserId?: string;
}


export const TaskDetailDialog: React.FC<TaskDetailDialogProps> = ({
  task,
  open,
  onOpenChange,
  onArchive,
  onDelete,
  onUpdateTask,
  columnMeta = {},
  boardName,
  enableUpvotes,
  onToggleUpvote,
  currentUserId,
}) => {
  const { currentWorkspace } = useWorkspace();
  const { user, profile } = useAuth();
  const [message, setMessage] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [currentPriority, setCurrentPriority] = useState<TaskPriority>("low");
  const [messages, setMessages] = useState<TaskMessage[]>([]);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editMessageText, setEditMessageText] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<TaskAttachment[]>([]);
  const [currentDueDate, setCurrentDueDate] = useState<string | null | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [teamMembers, setTeamMembers] = useState<WorkspaceMemberWithProfile[]>([]);
  const [taskCreatorId, setTaskCreatorId] = useState<string | null>(null);

  // Recurring task state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<'daily' | 'weekly' | 'monthly' | 'yearly'>('weekly');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [recurrenceDaysOfWeek, setRecurrenceDaysOfWeek] = useState<number[]>([]);
  const [recurrenceDayOfMonth, setRecurrenceDayOfMonth] = useState(1);
  const [recurrenceEndType, setRecurrenceEndType] = useState<'never' | 'after' | 'on_date'>('never');
  const [recurrenceOccurrences, setRecurrenceOccurrences] = useState(10);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | undefined>(undefined);
  const [recurrencePopoverOpen, setRecurrencePopoverOpen] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(false);

  // Fetch task creator ID when task changes
  useEffect(() => {
    const fetchCreator = async () => {
      if (task?.id) {
        try {
          const { data, error } = await supabase
            .from('tasks')
            .select('created_by')
            .eq('id', task.id)
            .single();

          if (!error && data?.created_by) {
            setTaskCreatorId(data.created_by);
          }
        } catch (error) {
          console.error('Error fetching task creator:', error);
        }
      } else {
        setTaskCreatorId(null);
      }
    };

    fetchCreator();
  }, [task?.id]);

  // Fetch workspace members when dialog opens
  useEffect(() => {
    if (open && currentWorkspace) {
      fetchWorkspaceMembers(currentWorkspace.id)
        .then(members => {
          setTeamMembers(members);
        })
        .catch(error => {
          console.error('Error fetching workspace members:', error);
        });
    }
  }, [open, currentWorkspace]);
  useEffect(() => {
    if (task) {
      setEditTitle(task.title);
      setEditDescription(task.description || "");
      setCurrentPriority(task.priority);
      setCurrentDueDate(task.dueDate);
      setIsEditingTitle(false);
      setIsEditingDescription(false);
      setIsDescriptionExpanded(false);

      // Load messages from database with user info
      const loadMessages = async () => {
        try {
          const messagesData = await taskService.getMessages(task.id);

          // Fetch user profiles for all message senders
          const userIds = [...new Set(messagesData.map((m: any) => m.user_id))];
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email, avatar_url')
            .in('id', userIds);

          const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);

          const formattedMessages: TaskMessage[] = messagesData.map((m: any) => {
            const userProfile = profilesMap.get(m.user_id);
            return {
              id: m.id,
              text: m.text,
              createdAt: m.created_at,
              userId: m.user_id,
              userName: userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || userProfile.email : 'Unknown',
              userAvatar: userProfile?.avatar_url,
              attachments: m.task_attachments?.map((a: any) => ({
                id: a.id,
                name: a.name,
                size: a.size,
                type: a.type,
                dataUrl: a.url,
                isExternalGif: a.is_external_gif || false,
              })) || [],
            };
          });

          setMessages(formattedMessages);
        } catch (error) {
          console.error('Error loading messages:', error);
          setMessages(task.messages || []);
        }
      };

      loadMessages();

      // Initialize recurring task state
      setIsRecurring(task.isRecurring || false);
      if (task.recurrencePattern) {
        setRecurrenceFrequency(task.recurrencePattern.frequency || 'weekly');
        setRecurrenceInterval(task.recurrencePattern.interval || 1);
        setRecurrenceDaysOfWeek(task.recurrencePattern.daysOfWeek || []);
        setRecurrenceDayOfMonth(task.recurrencePattern.dayOfMonth || 1);
        setRecurrenceEndType(task.recurrencePattern.endType || 'never');
        setRecurrenceOccurrences(task.recurrencePattern.occurrences || 10);
        setRecurrenceEndDate(task.recurrencePattern.endDate ? new Date(task.recurrencePattern.endDate) : undefined);
      } else {
        // Reset to defaults
        setRecurrenceFrequency('weekly');
        setRecurrenceInterval(1);
        setRecurrenceDaysOfWeek([]);
        setRecurrenceDayOfMonth(1);
        setRecurrenceEndType('never');
        setRecurrenceOccurrences(10);
        setRecurrenceEndDate(undefined);
      }
    }
  }, [task]);
  const genId = () => `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;



  const fileToAttachment = async (file: File): Promise<TaskAttachment> => {
    try {
      console.log('Uploading file to R2:', file.name, file.type, file.size);
      const { publicUrl } = await uploadToR2({
        fileName: file.name,
        file,
        folder: `task_attachments/${task?.id || 'new'}`
      });
      console.log('R2 upload successful:', publicUrl);
      return { id: `att_${genId()}`, name: file.name, size: file.size, type: file.type, dataUrl: publicUrl };
    } catch (error) {
      console.error('Error uploading task attachment:', error);
      // Return attachment without dataUrl if upload fails
      return { id: `att_${genId()}`, name: file.name, size: file.size, type: file.type };
    }
  };

  const allAttachments = React.useMemo(() =>
    messages.flatMap((m) => m.attachments || [])
    , [messages]);
  const handleArchive = async () => {
    if (!task) return;
    // Close dialog immediately to prevent blocking
    onOpenChange(false);
    // Use setTimeout to ensure the dialog state is processed before async operation
    setTimeout(async () => {
      try {
        await onArchive?.(task.id);
      } catch (error) {
        console.error('Error archiving task:', error);
      }
    }, 0);
  };

  const handleConfirmDelete = async () => {
    if (!task) return;
    // Close dialog immediately to prevent blocking
    onOpenChange(false);
    // Use setTimeout to ensure the dialog state is processed before async operation
    setTimeout(async () => {
      try {
        // Clean up any R2 attachments associated with this task before deleting
        if (allAttachments && allAttachments.length > 0) {
          for (const att of allAttachments) {
            if (att.dataUrl && att.dataUrl.includes('agencyplus.io')) {
              await deleteFromR2(att.dataUrl);
            }
          }
        }
        await onDelete?.(task.id);
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }, 0);
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      const atts = await Promise.all(files.map(fileToAttachment));
      setPendingAttachments((prev) => [...prev, ...atts]);
    }
    e.target.value = "";
  };
  const removePendingFile = (index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  };
  const handleSendMessage = async () => {
    if (!task || !user) return;
    if (!message.trim() && pendingAttachments.length === 0) return;

    try {
      // Save message to database with attachments
      const attachmentsData = pendingAttachments
        .filter(att => att.dataUrl) // Only include attachments that were successfully uploaded
        .map(att => ({
          name: att.name,
          size: att.size,
          type: att.type,
          url: att.dataUrl!,
          isExternalGif: att.isExternalGif || false,
        }));

      await taskService.addMessage(task.id, user.id, message.trim(), attachmentsData);

      // Clear input
      setMessage("");
      setPendingAttachments([]);

      // Refresh messages from database
      const updatedMessages = await taskService.getMessages(task.id);

      // Fetch user profiles for all message senders
      const userIds = [...new Set(updatedMessages.map((m: any) => m.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, avatar_url')
        .in('id', userIds);

      const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);

      const formattedMessages: TaskMessage[] = updatedMessages.map((m: any) => {
        const userProfile = profilesMap.get(m.user_id);
        return {
          id: m.id,
          text: m.text,
          createdAt: m.created_at,
          userId: m.user_id,
          userName: userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || userProfile.email : 'Unknown',
          userAvatar: userProfile?.avatar_url,
          attachments: m.task_attachments?.map((a: any) => ({
            id: a.id,
            name: a.name,
            size: a.size,
            type: a.type,
            dataUrl: a.url,
            isExternalGif: a.is_external_gif || false,
          })) || [],
        };
      });
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleAssigneesChange = async (assignees: any[]) => {
    if (!task) return;

    // Identify newly added assignees
    const currentAssigneeIds = task.assignees?.map(a => a.id) || [];
    const newAssignees = assignees.filter(a => !currentAssigneeIds.includes(a.id));

    const assigneesData = assignees.map(assignee => ({
      id: assignee.id,
      name: assignee.name,
      email: assignee.email,
      avatar: assignee.avatar
    }));

    onUpdateTask?.(task.id, { assignees: assigneesData });

    // Send email notifications to newly added assignees
    if (newAssignees.length > 0) {
      const assignerName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email : undefined;

      for (const assignee of newAssignees) {
        try {
          const { error } = await supabase.functions.invoke('send-task-assignment-email', {
            body: {
              email: assignee.email,
              assigneeName: assignee.name,
              taskTitle: task.title,
              taskLink: window.location.href,
              workspaceName: currentWorkspace?.name,
              assignedBy: assignerName,
              boardName: boardName,
              dueDate: task.dueDate,
              priority: task.priority,
            },
          });

          if (error) {
            console.error('Failed to send assignment email:', error);
          }
        } catch (error) {
          console.error('Error sending assignment email:', error);
        }
      }
    }
  };
  const startEditMessage = (id: string, text: string) => {
    setEditingMessageId(id);
    setEditMessageText(text);
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditMessageText("");
  };

  const saveEditedMessage = () => {
    if (!editingMessageId || !task) return;
    const newMessages = messages.map((m) => (m.id === editingMessageId ? { ...m, text: editMessageText } : m));
    setMessages(newMessages);
    onUpdateTask?.(task.id, { messages: newMessages });
    setEditingMessageId(null);
    setEditMessageText("");
  };

  const deleteMessage = async (id: string) => {
    if (!task) return;
    
    // Clean up any R2 attachments associated with this message
    const msgToDelete = messages.find(m => m.id === id);
    if (msgToDelete?.attachments) {
      for (const att of msgToDelete.attachments) {
        if (att.dataUrl && att.dataUrl.includes('agencyplus.io')) {
          await deleteFromR2(att.dataUrl);
        }
      }
    }

    const newMessages = messages.filter((m) => m.id !== id);
    setMessages(newMessages);
    onUpdateTask?.(task.id, { messages: newMessages });
  };

  const handleTitleDoubleClick = () => {
    if (!task) return;
    setIsEditingTitle(true);
    setEditTitle(task.title);
  };

  const handleSaveTitle = () => {
    if (!task) return;
    if (editTitle.trim() && editTitle !== task.title) {
      onUpdateTask?.(task.id, { title: editTitle.trim() });
    }
    setIsEditingTitle(false);
  };

  const handlePriorityChange = (priority: TaskPriority) => {
    if (!task) return;
    setCurrentPriority(priority);
    onUpdateTask?.(task.id, { priority });
  };

  const handleCancelTitleEdit = () => {
    if (!task) return;
    setEditTitle(task.title);
    setIsEditingTitle(false);
  };

  const handleEditDescription = () => {
    if (!task) return;
    setIsEditingDescription(true);
    setEditDescription(task.description || "");
  };

  const handleSaveDescription = () => {
    if (!task) return;
    if (editDescription !== task.description) {
      onUpdateTask?.(task.id, { description: editDescription });
    }
    setIsEditingDescription(false);
  };

  const handleCancelDescriptionEdit = () => {
    if (!task) return;
    setEditDescription(task.description || "");
    setIsEditingDescription(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveTitle();
    } else if (e.key === "Escape") {
      handleCancelTitleEdit();
    }
  };

  const getRecurrenceSummary = () => {
    if (!isRecurring) return null;

    let summary = `Every ${recurrenceInterval > 1 ? recurrenceInterval + ' ' : ''}${recurrenceFrequency}${recurrenceInterval > 1 && recurrenceFrequency !== 'daily' ? 's' : ''}`;

    if (recurrenceFrequency === 'weekly' && recurrenceDaysOfWeek.length > 0) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const days = recurrenceDaysOfWeek.map(d => dayNames[d]).join(', ');
      summary += ` on ${days}`;
    }

    if (recurrenceFrequency === 'monthly' && recurrenceDayOfMonth) {
      summary += ` on day ${recurrenceDayOfMonth}`;
    }

    return summary;
  };

  const handleUpdateRecurrence = () => {
    if (!task) return;

    if (!isRecurring) {
      // Remove recurrence
      onUpdateTask?.(task.id, {
        isRecurring: false,
        recurrencePattern: null
      });
      setRecurrencePopoverOpen(false);
      return;
    }

    // Build recurrence pattern
    const pattern: any = {
      frequency: recurrenceFrequency,
      interval: recurrenceInterval,
      endType: recurrenceEndType,
    };

    if (recurrenceFrequency === 'weekly') {
      pattern.daysOfWeek = recurrenceDaysOfWeek;
    }

    if (recurrenceFrequency === 'monthly') {
      pattern.dayOfMonth = recurrenceDayOfMonth;
    }

    if (recurrenceEndType === 'after') {
      pattern.occurrences = recurrenceOccurrences;
    }

    if (recurrenceEndType === 'on_date' && recurrenceEndDate) {
      pattern.endDate = recurrenceEndDate.toISOString();
    }

    onUpdateTask?.(task.id, {
      isRecurring: true,
      recurrencePattern: pattern
    });
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleCancelDescriptionEdit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          gap: 0,
          minHeight: '70vh',
          maxHeight: '85vh',
        }}
        className={cn(
          "overflow-hidden [&>button]:hidden border border-border bg-background dark:bg-card text-card-foreground transition-all",
          "max-md:max-w-full max-md:w-full max-md:h-full max-md:max-h-screen max-md:rounded-none max-md:inset-0 max-md:translate-x-0 max-md:translate-y-0 max-md:top-0 max-md:left-0",
          showSidePanel ? "max-w-5xl" : "max-w-3xl"
        )}
      >
        {!task ? (
          <div className="p-6 text-center text-muted-foreground">
            No task selected
          </div>
        ) : (
          <>
            {/* Title Header - Minimal Height */}
            <div className="flex items-center justify-between gap-4 px-6 pt-6 pb-3 border-b border-border flex-shrink-0 max-md:pt-2">
              <DialogDescription className="sr-only">
                Task details and management interface
              </DialogDescription>
              <div className="flex-1 min-w-0">
                {isEditingTitle ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={handleTitleKeyDown}
                      className="flex-1 text-xl font-semibold rounded px-2 py-1 focus:outline-none focus:ring-2 bg-background border border-input text-foreground placeholder:text-muted-foreground focus:ring-workspace-accent"
                      autoFocus
                      onFocus={(e) => e.target.select()}
                    />
                    <Button size="sm" variant="ghost" onClick={handleSaveTitle}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleCancelTitleEdit}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <DialogTitle
                    className="text-xl font-semibold text-left cursor-pointer hover:bg-accent/30 rounded px-1 -mx-1 py-1 truncate"
                    onDoubleClick={handleTitleDoubleClick}
                    title="Double-click to edit"
                  >
                    {editTitle}
                  </DialogTitle>
                )}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {enableUpvotes && (
                  <button
                    type="button"
                    onClick={() => onToggleUpvote?.(task.id)}
                    className={cn(
                      "inline-flex items-center justify-center gap-1.5 px-3 h-8 rounded-lg border transition-all active:scale-95 cursor-pointer select-none shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
                      Boolean(currentUserId && task.upvotedBy?.includes(currentUserId))
                        ? "bg-workspace-accent/10 border-workspace-accent text-workspace-accent shadow-xs"
                        : "bg-white dark:bg-card border-slate-200 dark:border-slate-700/80 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                    )}
                    title={Boolean(currentUserId && task.upvotedBy?.includes(currentUserId)) ? "Remove upvote" : "Upvote task"}
                    aria-label={`${task.upvotes ?? (task.upvotedBy?.length || 0)} upvotes. ${Boolean(currentUserId && task.upvotedBy?.includes(currentUserId)) ? "Remove upvote" : "Upvote task"}`}
                  >
                    <svg
                      viewBox="0 0 24 22"
                      fill={Boolean(currentUserId && task.upvotedBy?.includes(currentUserId)) ? "currentColor" : "none"}
                      className={cn(
                        "w-3.5 h-3.5 transition-colors",
                        Boolean(currentUserId && task.upvotedBy?.includes(currentUserId))
                          ? "text-workspace-accent fill-workspace-accent/20"
                          : "text-slate-600 dark:text-slate-400"
                      )}
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 4.5l7.5 13a1.5 1.5 0 0 1-1.3 2.2H5.8a1.5 1.5 0 0 1-1.3-2.2L12 4.5z" />
                    </svg>
                    <span
                      className={cn(
                        "text-xs font-bold leading-none tabular-nums",
                        Boolean(currentUserId && task.upvotedBy?.includes(currentUserId)) ? "text-workspace-accent" : "text-slate-900 dark:text-slate-100"
                      )}
                    >
                      {task.upvotes ?? (task.upvotedBy?.length || 0)}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => onUpdateTask?.(task.id, { completed: !task.completed, completedAt: !task.completed ? new Date().toISOString() : null })}
                  className="transition-all outline-none rounded-full"
                  title={task.completed ? "Mark as incomplete" : "Mark as complete"}
                  aria-label={task.completed ? "Mark as incomplete" : "Mark as complete"}
                >
                  <CircleCheckBig className={cn(
                    "h-6 w-6 transition-colors",
                    task.completed ? "text-green-500" : "text-muted-foreground/40"
                  )} />
                </button>
                <Select value={currentPriority} onValueChange={handlePriorityChange}>
                  <SelectTrigger
                    className="w-[110px] h-8 bg-background border-input text-foreground"
                    tabIndex={-1}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-foreground shadow-xl">
                    <SelectItem value="low">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-secondary"></div>
                        Low
                      </div>
                    </SelectItem>
                    <SelectItem value="medium">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-workspace-accent"></div>
                        Medium
                      </div>
                    </SelectItem>
                    <SelectItem value="high">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        High
                      </div>
                    </SelectItem>
                    <SelectItem value="critical">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-destructive"></div>
                        Critical
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Messages/Files Toggle Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("h-8 w-8", showSidePanel && "bg-accent")}
                  onClick={() => setShowSidePanel(!showSidePanel)}
                  title="Toggle messages and files"
                >
                  <MessageSquare className="h-4 w-4" />
                </Button>

                {/* Task Actions Kebab Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover border-border text-foreground">
                    <DropdownMenuItem onClick={handleArchive} className="gap-2">
                      <Archive className="h-4 w-4" />
                      Archive
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleConfirmDelete} className="gap-2 text-destructive">
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Body: flex row, fills all remaining height */}
            <div style={{ display: 'flex', flexDirection: 'row', flex: 1, overflow: 'hidden', padding: '0 24px' }}>
              {/* Left column: scrollable, fixed width when side panel open */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                width: showSidePanel ? '30%' : '100%',
                overflowY: 'auto',
                paddingBottom: 24,
                paddingRight: showSidePanel ? 16 : 0,
                flexShrink: 0,
              }}>
                {/* Fields Section - Compact */}
                <div className="space-y-3 flex-shrink-0 pt-4 pb-6">
                  <h3 className="font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Fields
                  </h3>

                  <div className={cn("gap-x-6 gap-y-3", showSidePanel ? "flex flex-col" : "grid grid-cols-2")}>
                    {/* Left Column */}
                    <div className="space-y-3">
                      {/* Created By Field */}
                      <div className="flex items-center gap-2 text-sm h-7">
                        <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground text-sm whitespace-nowrap">Created by</span>
                        {task.creator ? (
                          <div className="flex items-center gap-1.5">
                            <Avatar className="h-5 w-5">
                              <AvatarImage src={task.creator.avatar} alt={task.creator.name} />
                              <AvatarFallback className="text-[10px]">{task.creator.name.charAt(0).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm text-foreground truncate max-w-[120px]">{task.creator.name}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground italic">Unknown</span>
                        )}
                      </div>

                      {/* Due Date Field */}
                      <div className="flex items-center gap-2 text-sm h-7">
                        <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground text-sm whitespace-nowrap">Due Date</span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                "h-7 p-1 text-sm font-normal",
                                "hover:bg-muted",
                                !currentDueDate && "text-muted-foreground",
                                task && currentDueDate && isTaskOverdue({ ...task, dueDate: currentDueDate, status: task.status }) && "text-destructive"
                              )}
                            >
                              {currentDueDate ? format(new Date(currentDueDate), "MMM d, yyyy") : "Set date"}
                              <CalendarIcon className="ml-1 h-3 w-3" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-popover border-border text-foreground" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={currentDueDate ? new Date(currentDueDate) : undefined}
                              onSelect={(date) => {
                                const newDueDate = date ? date.toISOString() : undefined;
                                setCurrentDueDate(newDueDate);
                                onUpdateTask?.(task.id, { dueDate: newDueDate });
                              }}
                              disabled={(date) => {
                                if (isBefore(date, startOfDay(new Date()))) return true;
                                if (isBefore(date, startOfDay(new Date(task.createdAt)))) return true;
                                return false;
                              }}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Repeat Field */}
                      <div className="flex items-center gap-2 text-sm">
                        <Repeat className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground text-sm">Repeat</span>
                        <div className="flex-1">
                          {!isRecurring ? (
                            <Popover open={recurrencePopoverOpen} onOpenChange={setRecurrencePopoverOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 text-sm bg-workspace-accent text-white hover:bg-workspace-accent-hover hover:text-white"
                                  onClick={() => {
                                    setIsRecurring(true);
                                    setRecurrencePopoverOpen(true);
                                  }}
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Add
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-0 bg-popover border-border text-foreground" align="start">
                                <div className="p-4 space-y-4">
                                  <div className="flex items-center justify-between pb-3 border-b border-border">
                                    <h4 className="font-semibold text-sm text-foreground">Repeat Settings</h4>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => setRecurrencePopoverOpen(false)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>

                                  <>
                                  {/* Frequency */}
                                  <div className="space-y-2">
                                    <Label className="text-sm font-medium text-foreground">Repeat every</Label>
                                    <div className="flex gap-2">
                                      <Input
                                        type="number"
                                        min="1"
                                        max="365"
                                        value={recurrenceInterval}
                                        onChange={(e) => setRecurrenceInterval(parseInt(e.target.value) || 1)}
                                        className="bg-background border-input text-foreground w-16 h-9"
                                      />
                                      <Select value={recurrenceFrequency} onValueChange={(v: any) => setRecurrenceFrequency(v)}>
                                        <SelectTrigger className="bg-background border-input text-foreground flex-1 h-9">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-popover border border-border text-foreground">
                                          <SelectItem value="daily">Day{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                          <SelectItem value="weekly">Week{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                          <SelectItem value="monthly">Month{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                          <SelectItem value="yearly">Year{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>

                                  {/* Days of week */}
                                  {recurrenceFrequency === "weekly" && (
                                    <div className="space-y-2">
                                      <Label className="text-sm font-medium text-foreground">Repeat on</Label>
                                      <div className="grid grid-cols-7 gap-1">
                                        {[
                                          { label: "S", value: 0 },
                                          { label: "M", value: 1 },
                                          { label: "T", value: 2 },
                                          { label: "W", value: 3 },
                                          { label: "T", value: 4 },
                                          { label: "F", value: 5 },
                                          { label: "S", value: 6 },
                                        ].map((day) => (
                                          <label
                                            key={day.value}
                                            className={cn(
                                              "flex items-center justify-center h-8 rounded border cursor-pointer text-xs font-medium transition-colors",
                                              recurrenceDaysOfWeek.includes(day.value)
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-background border-input text-muted-foreground hover:bg-accent"
                                            )}
                                          >
                                            <input
                                              type="checkbox"
                                              className="sr-only"
                                              checked={recurrenceDaysOfWeek.includes(day.value)}
                                              onChange={(e) => {
                                                if (e.target.checked) {
                                                  setRecurrenceDaysOfWeek([...recurrenceDaysOfWeek, day.value].sort());
                                                } else {
                                                  setRecurrenceDaysOfWeek(recurrenceDaysOfWeek.filter(d => d !== day.value));
                                                }
                                              }}
                                            />
                                            {day.label}
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Day of month */}
                                  {recurrenceFrequency === "monthly" && (
                                    <div className="space-y-2">
                                      <Label className="text-sm font-medium text-foreground">Day of month</Label>
                                      <Input
                                        type="number"
                                        min="1"
                                        max="31"
                                        value={recurrenceDayOfMonth}
                                        onChange={(e) => setRecurrenceDayOfMonth(parseInt(e.target.value) || 1)}
                                        className="bg-background border-input text-foreground w-20 h-9"
                                      />
                                    </div>
                                  )}

                                  {/* End options */}
                                  <div className="space-y-2">
                                    <Label className="text-sm font-medium text-foreground">Ends</Label>
                                    <div className="space-y-1.5">
                                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input
                                          type="radio"
                                          name="end-type"
                                          checked={recurrenceEndType === "never"}
                                          onChange={() => setRecurrenceEndType("never")}
                                          className="h-3.5 w-3.5"
                                        />
                                        <span className="text-foreground">Never</span>
                                      </label>

                                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input
                                          type="radio"
                                          name="end-type"
                                          checked={recurrenceEndType === "after"}
                                          onChange={() => setRecurrenceEndType("after")}
                                          className="h-3.5 w-3.5"
                                        />
                                        <span className="text-foreground">After</span>
                                        {recurrenceEndType === "after" && (
                                          <>
                                            <Input
                                              type="number"
                                              min="1"
                                              max="999"
                                              value={recurrenceOccurrences}
                                              onChange={(e) => setRecurrenceOccurrences(parseInt(e.target.value) || 1)}
                                              className="bg-background border-input text-foreground w-16 h-7 text-xs"
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                            <span className="text-xs text-muted-foreground">times</span>
                                          </>
                                        )}
                                      </label>

                                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input
                                          type="radio"
                                          name="end-type"
                                          checked={recurrenceEndType === "on_date"}
                                          onChange={() => setRecurrenceEndType("on_date")}
                                          className="h-3.5 w-3.5"
                                        />
                                        <span className="text-foreground">On date</span>
                                      </label>
                                      {recurrenceEndType === "on_date" && (
                                        <div className="ml-6">
                                          <Popover>
                                            <PopoverTrigger asChild>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className={cn(
                                                  "h-7 text-xs font-normal bg-background border-input",
                                                  !recurrenceEndDate && 'text-muted-foreground'
                                                )}
                                              >
                                                <CalendarIcon className="mr-1 h-3 w-3" />
                                                {recurrenceEndDate ? format(recurrenceEndDate, "MMM d, yyyy") : "Pick date"}
                                              </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0 bg-popover border border-border" align="start">
                                              <CalendarComponent
                                                mode="single"
                                                selected={recurrenceEndDate}
                                                onSelect={(date) => setRecurrenceEndDate(date)}
                                                disabled={(date) => isBefore(date, startOfDay(new Date()))}
                                                initialFocus
                                              />
                                            </PopoverContent>
                                          </Popover>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="pt-3 border-t border-border flex gap-2">
                                    <Button
                                      size="sm"
                                      type="button"
                                      className="flex-1 h-8"
                                      onClick={() => {
                                        handleUpdateRecurrence();
                                        setRecurrencePopoverOpen(false);
                                      }}
                                    >
                                      Done
                                    </Button>
                                  </div>
                                </>
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <Popover open={recurrencePopoverOpen} onOpenChange={setRecurrencePopoverOpen}>
                          <PopoverTrigger asChild>
                            <div className="flex items-center gap-2 bg-accent/50 border border-border rounded-lg px-3 py-2 group hover:bg-accent transition-colors cursor-pointer">
                              <span className="text-sm font-medium text-foreground flex-1">
                                {getRecurrenceSummary()}
                              </span>
                              <button
                                type="button"
                                className="h-4 w-4 rounded-full flex items-center justify-center hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsRecurring(false);
                                  setRecurrenceDaysOfWeek([]);
                                  onUpdateTask?.(task.id, {
                                    isRecurring: false,
                                    recurrencePattern: null
                                  });
                                }}
                              >
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 p-0 bg-popover border-border text-foreground" align="start">
                            <div className="p-4 space-y-4">
                              <div className="flex items-center justify-between pb-3 border-b border-border">
                                <h4 className="font-semibold text-sm text-foreground">Repeat Settings</h4>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => setRecurrencePopoverOpen(false)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>

                              {/* Frequency */}
                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-foreground">Repeat every</Label>
                                <div className="flex gap-2">
                                  <Input
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={recurrenceInterval}
                                    onChange={(e) => setRecurrenceInterval(parseInt(e.target.value) || 1)}
                                    className="bg-background border-input text-foreground w-16 h-9"
                                  />
                                  <Select value={recurrenceFrequency} onValueChange={(v: any) => setRecurrenceFrequency(v)}>
                                    <SelectTrigger className="bg-background border-input text-foreground flex-1 h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-popover border border-border text-foreground">
                                      <SelectItem value="daily">Day{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                      <SelectItem value="weekly">Week{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                      <SelectItem value="monthly">Month{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                      <SelectItem value="yearly">Year{recurrenceInterval > 1 ? 's' : ''}</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>

                              {/* Days of week */}
                              {recurrenceFrequency === "weekly" && (
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-foreground">Repeat on</Label>
                                  <div className="grid grid-cols-7 gap-1">
                                    {[
                                      { label: "S", value: 0 },
                                      { label: "M", value: 1 },
                                      { label: "T", value: 2 },
                                      { label: "W", value: 3 },
                                      { label: "T", value: 4 },
                                      { label: "F", value: 5 },
                                      { label: "S", value: 6 },
                                    ].map((day) => (
                                      <label
                                        key={day.value}
                                        className={cn(
                                          "flex items-center justify-center h-8 rounded border cursor-pointer text-xs font-medium transition-colors",
                                          recurrenceDaysOfWeek.includes(day.value)
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-background border-input text-muted-foreground hover:bg-accent"
                                        )}
                                      >
                                        <input
                                          type="checkbox"
                                          className="sr-only"
                                          checked={recurrenceDaysOfWeek.includes(day.value)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setRecurrenceDaysOfWeek([...recurrenceDaysOfWeek, day.value].sort());
                                            } else {
                                              setRecurrenceDaysOfWeek(recurrenceDaysOfWeek.filter(d => d !== day.value));
                                            }
                                          }}
                                        />
                                        {day.label}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Day of month */}
                              {recurrenceFrequency === "monthly" && (
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-foreground">Day of month</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    max="31"
                                    value={recurrenceDayOfMonth}
                                    onChange={(e) => setRecurrenceDayOfMonth(parseInt(e.target.value) || 1)}
                                    className="bg-background border-input text-foreground w-20 h-9"
                                  />
                                </div>
                              )}

                              {/* End options */}
                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-foreground">Ends</Label>
                                <div className="space-y-1.5">
                                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                      type="radio"
                                      name="end-type-edit"
                                      checked={recurrenceEndType === "never"}
                                      onChange={() => setRecurrenceEndType("never")}
                                      className="h-3.5 w-3.5"
                                    />
                                    <span className="text-foreground">Never</span>
                                  </label>

                                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                      type="radio"
                                      name="end-type-edit"
                                      checked={recurrenceEndType === "after"}
                                      onChange={() => setRecurrenceEndType("after")}
                                      className="h-3.5 w-3.5"
                                    />
                                    <span className="text-foreground">After</span>
                                    {recurrenceEndType === "after" && (
                                      <>
                                        <Input
                                          type="number"
                                          min="1"
                                          max="999"
                                          value={recurrenceOccurrences}
                                          onChange={(e) => setRecurrenceOccurrences(parseInt(e.target.value) || 1)}
                                          className="bg-background border-input text-foreground w-16 h-7 text-xs"
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        <span className="text-xs text-muted-foreground">times</span>
                                      </>
                                    )}
                                  </label>

                                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                      type="radio"
                                      name="end-type-edit"
                                      checked={recurrenceEndType === "on_date"}
                                      onChange={() => setRecurrenceEndType("on_date")}
                                      className="h-3.5 w-3.5"
                                    />
                                    <span className="text-foreground">On date</span>
                                  </label>
                                  {recurrenceEndType === "on_date" && (
                                    <div className="ml-6">
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            type="button"
                                            className={cn(
                                              "h-7 text-xs font-normal bg-background border-input",
                                              !recurrenceEndDate && 'text-muted-foreground'
                                            )}
                                          >
                                            <CalendarIcon className="mr-1 h-3 w-3" />
                                            {recurrenceEndDate ? format(recurrenceEndDate, "MMM d, yyyy") : "Pick date"}
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0 bg-popover border border-border" align="start">
                                          <CalendarComponent
                                            mode="single"
                                            selected={recurrenceEndDate}
                                            onSelect={setRecurrenceEndDate}
                                            disabled={(date) => isBefore(date, startOfDay(new Date()))}
                                            initialFocus
                                          />
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="pt-3 border-t border-border flex gap-2">
                                <Button
                                  size="sm"
                                  type="button"
                                  className="flex-1 h-8"
                                  onClick={() => {
                                    handleUpdateRecurrence();
                                    setRecurrencePopoverOpen(false);
                                  }}
                                >
                                  Done
                                </Button>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-3">
                      {/* Status Field */}
                      <div className="flex items-center gap-2 text-sm h-7">
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground text-sm whitespace-nowrap">Status</span>
                        <Select
                          value={task.status}
                          onValueChange={(value) => onUpdateTask?.(task.id, { status: value })}
                        >
                          <SelectTrigger className="h-7 w-auto min-w-[100px] bg-background border-border text-sm">
                            <SelectValue>
                              <span className="capitalize">
                                {columnMeta[task.status]?.title || task.status}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-popover border-border text-foreground">
                            {Object.entries(columnMeta).map(([key, column]) => (
                              <SelectItem key={key} value={key} className="capitalize">
                                {column.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Time Tracker */}
                      <div className="h-7">
                        <TimeTracker taskId={task.id} />
                      </div>

                      {/* Assignees Field */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm h-7">
                          <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-muted-foreground text-sm whitespace-nowrap">Assignees</span>
                          <div>
                            <AssigneeSelector
                              currentAssignees={task?.assignees}
                              onAssigneesChange={handleAssigneesChange}
                              teamMembers={teamMembers}
                              dropdownDirection="top"
                              excludeUserIds={taskCreatorId ? [taskCreatorId] : []}
                            />
                          </div>
                        </div>

                        {/* Assigned list */}
                        {task?.assignees && task.assignees.length > 0 && (
                          <div className="space-y-1.5 pl-6">
                            {task.assignees.map((assignee) => (
                              <div key={assignee.id} className="flex items-center gap-2 group">
                                <Avatar className="h-5 w-5">
                                  <AvatarImage src={assignee.avatar} alt={assignee.name} />
                                  <AvatarFallback className="text-[10px]">
                                    {assignee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-xs flex-1 text-foreground">
                                  {assignee.name}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className={cn(
                                    "h-4 w-4 p-0 opacity-0 group-hover:opacity-100 transition-opacity",
                                    "hover:bg-muted text-muted-foreground hover:text-foreground"
                                  )}
                                  onClick={() => {
                                    const updatedAssignees = task.assignees?.filter(a => a.id !== assignee.id) || [];
                                    handleAssigneesChange(updatedAssignees);
                                  }}
                                >
                                  <X className="h-2.5 w-2.5" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator className="bg-border" />

                {/* Description Section - Grows to fill space */}
                <div className="space-y-3 flex-1 min-h-0 overflow-y-auto no-scrollbar pt-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">
                      Description
                    </h3>
                    {!isEditingDescription && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleEditDescription}
                        className="h-6 px-2 text-xs"
                      >
                        <Edit2 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>

                  {isEditingDescription ? (
                    <div className="space-y-2">
                      <Textarea
                        className="bg-background border-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        onKeyDown={handleDescriptionKeyDown}
                        placeholder="Enter task description..."
                        style={{ minHeight: "100px", resize: "vertical" }}
                        autoFocus
                      />
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={handleCancelDescriptionEdit} className="border-border text-foreground hover:bg-muted">
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                        <Button size="sm" onClick={handleSaveDescription} className="bg-workspace-accent text-white hover:bg-workspace-accent-hover">
                          <Check className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {editDescription ? (
                        <div className="space-y-2">
                          <div className="text-base leading-relaxed p-3 rounded-md bg-muted/50 text-foreground">
                            {(() => {
                              const words = editDescription.trim().split(/\s+/);
                              const wordLimit = 30;
                              const shouldTruncate = words.length > wordLimit;

                              if (!shouldTruncate) {
                                return editDescription;
                              }

                              if (isDescriptionExpanded) {
                                return editDescription;
                              }

                              return words.slice(0, wordLimit).join(' ') + '...';
                            })()}
                          </div>
                          {(() => {
                            const words = editDescription.trim().split(/\s+/);
                            const shouldShowToggle = words.length > 30;

                            if (!shouldShowToggle) return null;

                            return (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                              >
                                {isDescriptionExpanded ? (
                                  <>
                                    <ChevronUp className="h-3 w-3 mr-1" />
                                    Show Less
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-3 w-3 mr-1" />
                                    Show More
                                  </>
                                )}
                              </Button>
                            );
                          })()}
                        </div>
                      ) : (
                        <p className="text-base italic text-muted-foreground">No description provided</p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Right panel: messages + composer */}
              {showSidePanel && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  borderLeft: '1px solid hsl(var(--border))',
                  paddingLeft: 16,
                  overflow: 'hidden',
                }}>
                  <Tabs defaultValue="messages" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                    <TabsList style={{ display: 'flex', flexDirection: 'row', flexShrink: 0, width: '100%', marginTop: 12 }} className="bg-muted dark:bg-background/50 border-border p-1 rounded-lg">
                    <TabsTrigger value="messages" className="gap-2 flex-1 data-[state=active]:bg-background dark:data-[state=active]:bg-card data-[state=active]:shadow-sm rounded-md">
                      <MessageSquare className="h-4 w-4" />
                      Messages
                    </TabsTrigger>
                    <TabsTrigger value="files" className="gap-2 flex-1 data-[state=active]:bg-background dark:data-[state=active]:bg-card data-[state=active]:shadow-sm rounded-md">
                      <Paperclip className="h-4 w-4" />
                      Files
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent
                    value="messages"
                    style={{ flexDirection: 'column', flex: 1, overflow: 'hidden', paddingTop: 16, marginTop: 0 }}
                    className="data-[state=active]:flex"
                  >
                    {/* Scrollable messages list */}
                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }} className="no-scrollbar space-y-2">
                      {messages.length === 0 ? (
                        <div className="flex items-center justify-center" style={{ minHeight: '150px' }}>
                          <p className="text-sm text-center text-muted-foreground">
                            No messages yet. Start a conversation about this task.
                          </p>
                        </div>
                      ) : (
                        messages.map((m) => {
                          const isCurrentUser = user ? m.userId === user.id : false;
                          const isEditing = editingMessageId === m.id;

                          return (
                            <div
                              key={m.id}
                              className="group relative flex gap-3 px-3 py-2 rounded-lg transition-colors duration-200 hover:bg-card/50"
                            >
                              <div className="relative shrink-0 self-start">
                                <Avatar className="w-9 h-9 mt-0.5">
                                  <AvatarImage src={m.userAvatar} alt={m.userName || 'User'} />
                                  <AvatarFallback className="text-sm bg-card text-muted-foreground">
                                    {m.userName?.charAt(0) || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                {/* Online indicator - only show for other users */}
                                {!isCurrentUser && (
                                  <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 bg-green-500 border-background" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2">
                                  <span className="font-bold text-sm text-foreground">
                                    {isCurrentUser ? 'You' : m.userName}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {formatDistanceToNow(new Date(m.createdAt), { addSuffix: true })}
                                  </span>
                                </div>

                                {isEditing ? (
                                  <div className="mt-1 max-w-[600px]">
                                    <Textarea
                                      value={editMessageText}
                                      onChange={(e) => setEditMessageText(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                          e.preventDefault();
                                          saveEditedMessage();
                                        } else if (e.key === 'Escape') {
                                          cancelEditMessage();
                                        }
                                      }}
                                      className="min-h-[44px] max-h-[200px] resize-none text-sm border-2 focus-visible:ring-0 bg-background border-border text-foreground"
                                      autoFocus
                                      rows={1}
                                    />
                                    <div className="flex items-center gap-1 mt-1">
                                      <Button size="sm" variant="ghost" className="h-7 text-xs hover:bg-card text-muted-foreground" onClick={cancelEditMessage}>
                                        <X className="w-3 h-3 mr-1" /> Cancel
                                      </Button>
                                      <Button size="sm" className="h-7 text-xs bg-workspace-accent hover:bg-workspace-accent-hover text-white" onClick={saveEditedMessage} disabled={!editMessageText.trim()}>
                                        <Check className="w-3 h-3 mr-1" /> Save
                                      </Button>
                                      <span className="text-[10px] ml-2 text-muted-foreground">Esc to cancel · Enter to save</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="mt-1 text-sm text-foreground">
                                    {m.text && (
                                      <ReactMarkdown
                                        components={{
                                          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                                          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                                          em: ({ children }) => <em className="italic">{children}</em>,
                                          code: ({ children }) => <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                                          a: ({ children, href }) => (
                                            <a href={href} target="_blank" rel="noreferrer" className="text-workspace-accent hover:underline">
                                              {children}
                                            </a>
                                          ),
                                          ul: ({ children }) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                                          ol: ({ children }) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                                        }}
                                      >
                                        {m.text}
                                      </ReactMarkdown>
                                    )}

                                    {m.attachments && m.attachments.length > 0 && (
                                      <div className="flex flex-wrap gap-2 mt-2">
                                        {m.attachments.map((a) => {
                                          const isImage = a.type?.startsWith("image") || a.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                          return isImage && a.dataUrl ? (
                                            <div key={a.id} className="relative group/img">
                                              <a href={a.dataUrl} target="_blank" rel="noreferrer" className="block rounded-md overflow-hidden hover:opacity-90 transition-opacity">
                                                <img src={a.dataUrl} alt={a.name} className="max-w-sm max-h-64 object-contain rounded-md border border-border" />
                                              </a>
                                              <a
                                                href={a.dataUrl}
                                                download={a.name}
                                                className="absolute top-2 right-2 opacity-0 group-hover/img:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded px-2 py-1 text-xs"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                Download
                                              </a>
                                            </div>
                                          ) : (
                                            <a
                                              key={a.id}
                                              href={a.dataUrl}
                                              download={a.name}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-muted hover:bg-muted/80 transition-colors"
                                            >
                                              <FileText className="h-4 w-4 text-muted-foreground" />
                                              <span className="text-xs truncate max-w-[200px]">{a.name}</span>
                                            </a>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Message actions */}
                              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-6 w-6">
                                      <MoreVertical className="h-3 w-3" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => startEditMessage(m.id, m.text)}>
                                      <Edit2 className="h-3 w-3 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => deleteMessage(m.id)} className="text-destructive">
                                      <Trash2 className="h-3 w-3 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Composer - pinned to bottom */}
                    <div style={{ flexShrink: 0, paddingBottom: 16, paddingTop: 8 }}>
                      <TaskMessageComposer
                        message={message}
                        setMessage={setMessage}
                        onSend={handleSendMessage}
                        onAttachClick={handleAttachClick}
                        pendingAttachments={pendingAttachments}
                        onRemoveAttachment={removePendingFile}
                        onAddAttachment={(attachment) => setPendingAttachments((prev) => [...prev, attachment])}
                        fileInputRef={fileInputRef}
                        onFilesSelected={handleFilesSelected}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent
                    value="files"
                    style={{ flexDirection: 'column', flex: 1, overflow: 'hidden', paddingTop: 16, marginTop: 0 }}
                    className="data-[state=active]:flex"
                  >
                    <div className="flex-1 overflow-y-auto no-scrollbar min-h-0">
                      {allAttachments.length === 0 ? (
                        <p className="text-sm text-center py-8 text-muted-foreground">No files attached yet.</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pr-1">
                          {allAttachments.map((a) => (
                            a.type?.startsWith("image") && a.dataUrl ? (
                              <a key={a.id} href={a.dataUrl} target="_blank" rel="noreferrer" className="block">
                                <img src={a.dataUrl} alt={a.name} className="h-24 w-full object-cover rounded border" />
                                <span className="block text-[10px] mt-1 truncate text-muted-foreground">{a.name}</span>
                              </a>
                            ) : (
                              <a key={a.id} href={a.dataUrl} download={a.name} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded border h-8 flex items-center bg-muted border-border text-foreground hover:bg-muted/80">
                                {a.name}
                              </a>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  </Tabs>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};